import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** ACH50 = Q50 · 60 / V — house silhouette, blower door, 50 Pa gauge. */
export function DiagramBETAirChangesPerHour() {
  // House outline
  const hx = 70, hy = 90, hw = 220, hh = 130;

  return (
    <DiagramCanvas>
      {/* Roof */}
      <Polygon points={`${hx - 8},${hy} ${hx + hw / 2},${hy - 44} ${hx + hw + 8},${hy}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Chimney */}
      <Rect x={hx + hw - 50} y={hy - 40} width={16} height={24} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />

      {/* Body */}
      <Rect x={hx} y={hy} width={hw} height={hh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Windows */}
      {[hx + 20, hx + 160].map((x, i) => (
        <G key={i}>
          <Rect x={x} y={hy + 20} width={34} height={30} fill="rgba(122,170,187,0.12)" stroke={DT.stroke} strokeWidth="1" />
          <Line x1={x + 17} y1={hy + 20} x2={x + 17} y2={hy + 50} stroke={DT.stroke} strokeWidth="0.7" />
          <Line x1={x} y1={hy + 35} x2={x + 34} y2={hy + 35} stroke={DT.stroke} strokeWidth="0.7" />
        </G>
      ))}

      {/* Door with blower door insert */}
      <Rect x={hx + 84} y={hy + 30} width={50} height={hh - 30} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.4" />
      <Rect x={hx + 88} y={hy + 34} width={42} height={92} fill="rgba(255,107,26,0.08)" stroke={DT.varColor} strokeWidth="1.2" />
      <Circle cx={hx + 109} cy={hy + 82} r={16} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      {/* Fan blades */}
      {[0, 60, 120, 180, 240, 300].map(d => {
        const r = d * Math.PI / 180;
        return <Path key={d} d={`M${hx + 109},${hy + 82} L${hx + 109 + 14 * Math.cos(r)},${hy + 82 + 14 * Math.sin(r)}`}
          stroke={DT.varColor} strokeWidth="1.4" />;
      })}
      <Circle cx={hx + 109} cy={hy + 82} r={3} fill={DT.varColor} />
      <SvgText x={hx + 109} y={hy + 116} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">BLOWER DOOR</SvgText>

      {/* V label inside */}
      <Rect x={hx + 150} y={hy + 80} width={56} height={22} fill="rgba(0,0,0,0.55)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={hx + 178} y={hy + 94} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V (ft³)</SvgText>

      {/* 50 Pa manometer */}
      <Circle cx={40} cy={160} r={22} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      <Circle cx={40} cy={160} r={17} fill="none" stroke={DT.varColor} strokeWidth="0.5" />
      {[-60, 0, 60].map(d => {
        const r = (d - 90) * Math.PI / 180;
        return <Line key={d} x1={40 + 14 * Math.cos(r)} y1={160 + 14 * Math.sin(r)}
          x2={40 + 20 * Math.cos(r)} y2={160 + 20 * Math.sin(r)} stroke={DT.varColor} strokeWidth="0.8" />;
      })}
      {/* Needle pointing to 50 */}
      <Line x1={40} y1={160} x2={40 + 14} y2={160 - 10} stroke={DT.varColor} strokeWidth="1.8" />
      <Circle cx={40} cy={160} r={2.5} fill={DT.varColor} />
      <SvgText x={40} y={194} fill={DT.varColor} fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">50 Pa</SvgText>
      <Line x1={62} y1={160} x2={hx + 92} y2={hy + 82} stroke={DT.leader} strokeWidth="0.7" strokeDasharray="2,2" />

      {/* Air exit arrows at blower */}
      {[hy + 74, hy + 82, hy + 90].map((y, i) => (
        <G key={i}>
          <Line x1={hx + 90} y1={y} x2={hx + 70} y2={y} stroke={DT.arrow} strokeWidth="1.2" />
          <Polygon points={`${hx + 70},${y} ${hx + 76},${y - 3} ${hx + 76},${y + 3}`} fill={DT.arrow} />
        </G>
      ))}

      {/* Ground */}
      <Line x1={20} y1={hy + hh} x2={330} y2={hy + hh} stroke={DT.dim} strokeWidth="1.4" />
      {Array.from({ length: 14 }).map((_, i) => (
        <Line key={i} x1={25 + i * 22} y1={hy + hh + 2} x2={35 + i * 22} y2={hy + hh + 8} stroke={DT.dim} strokeWidth="0.7" />
      ))}

      {/* Labels */}
      <LeaderLine x1={hx + 178} y1={hy + 90} x2={335} y2={60} varName="V" desc="conditioned volume" anchor="end" />
      <LeaderLine x1={40} y1={138} x2={20} y2={60} varName="Q_50" desc="fan flow @ 50 Pa" anchor="start" />
      <LeaderLine x1={hx + 109} y1={hy + 98} x2={325} y2={230} varName="ACH50" desc="air changes/hr" anchor="end" />

      <FormulaTag formula="ACH50 = Q_50 · 60 / V    (CFM · min/hr / ft³)" />
    </DiagramCanvas>
  );
}
